import { CASH_PAYMENT_VALUES } from '@/constants/paymentMethods';

/**
 * Règles "Conseils de gestion" (source unique — voir OrderSidePanel2.ManagementAdviceSection
 * pour le rendu, orderService2 pour la classification par lot / la navigation "Suivant").
 * Ne pas redupliquer cette logique ailleurs.
 */
export type OrderAdviceRuleKey =
  | 'ready_to_close' // Règle 1 : Prêt à cloturer
  | 'do_not_process' // Règle 2 : Ne pas traiter
  | 'to_process' // Règle 3 : À traiter
  | 'empty_document' // Règle 5 : Document vide
  | 'can_be_deleted' // Règle 6 : Peut être supprimé
  | 'mixed_to_process' // Règle 9 : Document à traiter
  | 'not_yet_delivered' // Règle 8 : Pas encore livré
  | 'paid_cash'; // Règle 7 : Payé en liquide

/** Conseils qui n'impliquent aucune action manuelle immédiate : safe à sauter automatiquement. */
export const SKIPPABLE_ADVICE_KEYS = new Set<OrderAdviceRuleKey>([
  'do_not_process',
  'empty_document',
  'not_yet_delivered',
]);

export interface AdviceCashPayment {
  amount: number;
  payment_method: string | null;
}

interface ComputeOrderAdviceKeysParams {
  /** total_ttc - somme des paiements (déjà calculé côté appelant) */
  solde: number;
  payments: AdviceCashPayment[];
  deliveryDate?: string | null;
  deliveryTiming?: string | null;
  isOrderClosed: boolean;
}

// Normalise dd/mm/yyyy ou yyyy-mm-dd → ISO yyyy-mm-dd (sans drift de timezone)
function toIsoDate(s?: string | null): string | null {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [, d, mo, y] = m;
  return `${y}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`;
}

/** Retourne la liste des conseils "Conseils de gestion" qui s'appliquent, dans l'ordre d'affichage. */
export function computeOrderAdviceKeys({
  solde,
  payments,
  deliveryDate,
  deliveryTiming,
  isOrderClosed,
}: ComputeOrderAdviceKeysParams): OrderAdviceRuleKey[] {
  if (isOrderClosed) return [];

  let cash = 0;
  let bank = 0;
  for (const p of payments) {
    const isCash = p.payment_method != null && CASH_PAYMENT_VALUES.has(p.payment_method);
    if (isCash) cash += p.amount;
    else bank += p.amount;
  }
  let mode: 'none' | 'cash' | 'bank' | 'mixed' = 'none';
  if (payments.length === 0) mode = 'none';
  else if (cash > 0 && bank === 0) mode = 'cash';
  else if (bank > 0 && cash === 0) mode = 'bank';
  else mode = 'mixed';

  const soldeIsZero = Math.abs(solde) <= 0.01;

  const dateIso = toIsoDate(deliveryDate);
  const todayIso = new Date().toISOString().split('T')[0];
  let status: 'overdue' | 'today' | 'future' | 'timing' | 'none' = 'none';
  if (dateIso && dateIso < todayIso) status = 'overdue';
  else if (dateIso && dateIso === todayIso) status = 'today';
  else if (dateIso) status = 'future';
  else if (deliveryTiming) status = 'timing';

  const timingRaw = deliveryTiming || undefined;
  const timingLower = timingRaw?.toLowerCase();
  const isLaterTiming = !!(
    timingRaw &&
    (timingRaw === '22f3b781-268f-4c38-9046-3cdebd0c45d3' ||
      timingRaw === 'a9c03b64-aaf9-403b-ac09-29c23dc9ac3c' ||
      timingLower === 'a confirmer ultérieurement' ||
      timingLower === 'a emporter ultérieurement')
  );
  const isPickupByClient = !!(
    timingRaw &&
    (timingRaw === 'a95087c5-a08c-4e65-ab12-22dca82af4a9' ||
      timingLower === 'emporté par le client' ||
      timingLower === 'emporte par le client')
  );
  const isTTCZero = soldeIsZero && payments.length === 0;
  const isReadyToClose = soldeIsZero && mode === 'bank';

  const keys: OrderAdviceRuleKey[] = [];

  if (isReadyToClose) keys.push('ready_to_close');

  const isLaterUnresolved = isLaterTiming && !isTTCZero && !isReadyToClose;
  const isMixedNotDelivered = mode === 'mixed' && status !== 'overdue';
  if ((isMixedNotDelivered || isLaterUnresolved) && !isPickupByClient) keys.push('do_not_process');

  if ((solde > 0.01 && status === 'overdue') || (isPickupByClient && !isReadyToClose)) {
    keys.push('to_process');
  }

  if (isTTCZero) keys.push('empty_document');

  if (soldeIsZero && mode === 'cash' && status === 'overdue') keys.push('can_be_deleted');

  if (status === 'overdue' && mode === 'mixed') keys.push('mixed_to_process');

  if (
    status !== 'overdue' &&
    !isLaterTiming &&
    !isTTCZero &&
    !isPickupByClient &&
    !isReadyToClose
  ) {
    keys.push('not_yet_delivered');
  }

  if (soldeIsZero && mode === 'cash' && status !== 'overdue') keys.push('paid_cash');

  return keys;
}

/** true si TOUS les conseils qui s'appliquent sont "safe à sauter" (aucune action manuelle requise). */
export function isOrderSkippable(keys: OrderAdviceRuleKey[]): boolean {
  return keys.length > 0 && keys.every(k => SKIPPABLE_ADVICE_KEYS.has(k));
}
